import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ChevronLeft, Trash2 } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Avatar } from "../../components/Avatar/Avatar";
import { Button } from "../../components/Button/Button";
import { CountryCell } from "../../components/CountryCell/CountryCell";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { useToast } from "../../components/Toast/toastContext";

import { ApiError, describeError } from "../../lib/errors";
import { useProject } from "../projects/useProject";
import type { GuildAssetsConfig, GuildConfig } from "../projects/config/types";
import { getGuild } from "./api";
import { DeleteGuildDialog } from "./DeleteGuildDialog";
import { GuildBlockedTable } from "./GuildBlockedTable";
import {
  blocksQueryKey,
  guildQueryKey,
  guildsListQueryKey,
} from "./queryKeys";
import { GuildChatFeed } from "./GuildChatFeed";
import { GuildMembersTable } from "./GuildMembersTable";
import { GuildSettingsForm } from "./GuildSettingsForm";
import type { GuildDetail } from "./types";
import { visibilityLabel, visibilityTone } from "./visibility";
import styles from "./GuildDetailPage.module.css";

// === Formatting helpers ============================================== //
// Copied from PlayerDetailPage — consistent with the players feature, which
// keeps a local copy rather than a shared module.

const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

/** "Feb 28, 2026" — used for the Created field of the identity card. */
function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return DATE_FMT.format(d);
}

/**
 * Compact relative time — "just now", "2 hours ago", "1 day ago", or an
 * absolute date past a week. Used for the "Last updated" identity field.
 */
function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return plural(min, "minute");
  const hr = Math.floor(min / 60);
  if (hr < 24) return plural(hr, "hour");
  const day = Math.floor(hr / 24);
  if (day < 7) return plural(day, "day");
  return DATE_FMT.format(d);
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? "" : "s"} ago`;
}

// === Component ====================================================== //

/**
 * The guild detail page — ports docs/mockups/guild-detail.html screen 1. It
 * orchestrates a read-only identity header plus the three section components:
 * GuildSettingsForm, GuildMembersTable and GuildChatFeed. The guild query is
 * the shared baseline: the Settings form re-syncs it via `onSaved`, the Members
 * table invalidates it via `onChanged`.
 */
export function GuildDetailPage() {
  const { id = "", guildId = "" } = useParams<{
    id: string;
    guildId: string;
  }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { push } = useToast();

  // The disband dialog, opened from the danger zone at the bottom of the
  // column. It needs nothing but the loaded guild, so it is mounted only once
  // somebody asks for it.
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Bumped after a save to remount the settings form, so its uncontrolled
  // editors re-seed from the freshly-saved record — the PlayerDetailPage
  // `saveGen` idiom.
  const [saveGen, setSaveGen] = useState(0);

  // The project — shared cache key with GuildsListPage / other project pages.
  // Used only for the breadcrumb name.
  const projectQuery = useProject(id);

  // The guild record + its members.
  const guildQuery = useQuery({
    queryKey: guildQueryKey(id, guildId),
    queryFn: () => getGuild(id, guildId),
    enabled: !!id && !!guildId,
  });

  const project = projectQuery.data;
  const guild = guildQuery.data;

  // games.config is typed `unknown` — narrow to the sub-objects this page
  // needs. The Metadata card draws its form from the schema, and tags the one
  // key the recommendation feed gates on; `guild_assets` is a sibling key of
  // `guild`, not a part of it, and holds the logo whitelist.
  const config = project?.config as
    | { guild?: GuildConfig; guild_assets?: GuildAssetsConfig }
    | undefined;
  const guildConfig = config?.guild;
  const metadataSchema = guildConfig?.metadata_schema ?? [];
  const requirementKey =
    guildConfig?.recommendations?.requirement_guild_key ?? "";
  const allowedLogos = config?.guild_assets?.allowed_logos ?? [];

  const header = (
    <PageHeader
      breadcrumb={[
        { label: project?.name ?? "Project" },
        { label: "Guilds", to: `/projects/${id}/guilds` },
        { label: guild?.name ?? "Guild" },
      ]}
      title={guild?.name ?? "Guild"}
      actions={
        <Link to={`/projects/${id}/guilds`} className={styles.backBtn}>
          <ChevronLeft size={13} aria-hidden />
          Back to guilds
        </Link>
      }
    />
  );

  // === Not-found state =============================================== //
  if (
    guildQuery.isError &&
    guildQuery.error instanceof ApiError &&
    guildQuery.error.status === 404
  ) {
    return (
      <>
        {header}
        <div className={styles.page}>
          <div className={styles.content}>
            <div className={styles.errorCard}>
              <Alert variant="danger" title="Guild not found">
                This guild doesn&apos;t exist, or it belongs to another
                project.
              </Alert>
            </div>
          </div>
        </div>
      </>
    );
  }

  // === Generic load error =========================================== //
  if (projectQuery.isError || guildQuery.isError) {
    const err =
      guildQuery.error instanceof ApiError
        ? guildQuery.error
        : projectQuery.error instanceof ApiError
          ? projectQuery.error
          : null;
    return (
      <>
        {header}
        <div className={styles.page}>
          <div className={styles.content}>
            <div className={styles.errorCard}>
              <Alert variant="danger" title="Couldn't load guild">
                {err ? describeError(err.code) : describeError("internal")}
              </Alert>
              <div className={styles.errorActions}>
                <Button
                  variant="secondary"
                  onClick={() => {
                    void projectQuery.refetch();
                    void guildQuery.refetch();
                  }}
                >
                  Retry
                </Button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // === Loading skeleton ============================================= //
  if (projectQuery.isLoading || guildQuery.isLoading || !project || !guild) {
    return (
      <>
        {header}
        <div className={styles.page}>
          <div className={styles.content}>
            <LoadingSkeleton />
          </div>
        </div>
      </>
    );
  }

  // What the operator leaves with, worded while the guild is still loaded —
  // by the time the toast is pushed the record it describes is gone.
  const deletedToast = `${guild.name} deleted — ${guild.member_count} ${
    guild.member_count === 1 ? "member was" : "members were"
  } let go.`;

  /**
   * The guild is gone from the server, so this card has no record behind it:
   * the list is the only honest destination. `deletedHere` is false on the
   * dead end — somebody deleted the guild before us, and announcing their
   * deletion as ours would be a lie.
   */
  function leaveForList(deletedHere: boolean) {
    // Thrown away, not marked stale: the list is not mounted right now, so an
    // invalidation would only flag it, and a page that mounts onto a flagged
    // cache draws that cache first — one frame of a table with this guild
    // still in it. A skeleton costs the project's cached pages and searches;
    // it does not cost the operator their belief in what they just did.
    qc.removeQueries({ queryKey: guildsListQueryKey(id) });
    void qc.invalidateQueries({ queryKey: ["project", id, "activity"] });
    qc.removeQueries({ queryKey: guildQueryKey(id, guildId) });
    navigate(`/projects/${id}/guilds`);
    if (deletedHere) {
      push({ variant: "success", message: deletedToast });
    }
  }

  return (
    <>
      {header}
      <div className={styles.page}>
        <div className={styles.content}>
          {/* ───────── Identity (read-only) ───────── */}
          <div className={styles.card}>
            <div className={styles.cardBody}>
              <div className={styles.identity}>
                <span className={styles.emLg}>
                  <Avatar name={guild.name} size={52} />
                </span>
                <div>
                  <div className={styles.identityName}>{guild.name}</div>
                  <div className={styles.identitySub}>
                    <VisibilityBadge visibility={guild.visibility} />
                    <span>
                      · {guild.member_count}{" "}
                      {guild.member_count === 1 ? "member" : "members"}
                    </span>
                  </div>
                </div>
              </div>
              <div className={styles.kvGrid}>
                <div className={styles.kv}>
                  <div className={styles.kvKey}>Guild UUID</div>
                  <div className={`${styles.kvVal} ${styles.mono}`}>
                    {guild.id}
                  </div>
                </div>
                {/* Logo — the raw asset ID, shown rather than resolved: no
                    picture exists for it on this side, and it is deliberately
                    read straight from the column instead of from the Settings
                    select below, which can only offer the project's whitelist.
                    A guild wearing a logo outside that list is normal, and this
                    row is where the operator can see which one. */}
                <div className={styles.kv}>
                  <div className={styles.kvKey}>Logo</div>
                  <div className={`${styles.kvVal} ${styles.mono}`}>
                    {guild.logo || "—"}
                  </div>
                </div>
                <div className={styles.kv}>
                  <div className={styles.kvKey}>Country</div>
                  <div className={styles.kvVal}>
                    <CountryCell code={guild.country} variant="name" />
                  </div>
                </div>
                <div className={styles.kv}>
                  <div className={styles.kvKey}>Created</div>
                  <div className={styles.kvVal}>
                    {formatDate(guild.created_at)}
                  </div>
                </div>
                <div className={styles.kv}>
                  <div className={styles.kvKey}>Last updated</div>
                  <div className={styles.kvVal}>
                    {formatRelative(guild.updated_at)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ───────── Settings ───────── */}
          {/* Keyed on a counter this page bumps after ITS OWN save, not on
              `updated_at`. Every guild mutation moves that column — kicking a
              member, promoting one — and every one of them invalidates this
              query, so a key on it remounted the form (and the Metadata editor
              inside it, and the review dialog if it was open) out from under
              whoever was typing. Three settings fields were cheap to lose; a
              hand-written JSON object is not. */}
          <GuildSettingsForm
            key={saveGen}
            projectId={id}
            guildId={guildId}
            guild={guild}
            metadataSchema={metadataSchema}
            requirementKey={requirementKey}
            allowedLogos={allowedLogos}
            onSaved={(updated) => {
              setSaveGen((g) => g + 1);
              qc.setQueryData<GuildDetail>(guildQueryKey(id, guildId), updated);
              void qc.invalidateQueries({
                queryKey: guildQueryKey(id, guildId),
              });
              void qc.invalidateQueries({
                queryKey: ["project", id, "activity"],
              });
            }}
          />

          {/* ───────── Members ───────── */}
          {/* A kick both removes a member and blocks them, so the Blocked
              section below has to refetch alongside the roster. */}
          <GuildMembersTable
            projectId={id}
            guildId={guildId}
            guild={guild}
            onChanged={() => {
              void qc.invalidateQueries({ queryKey: guildQueryKey(id, guildId) });
              void qc.invalidateQueries({
                queryKey: blocksQueryKey(id, guildId),
              });
              void qc.invalidateQueries({
                queryKey: ["project", id, "activity"],
              });
            }}
          />

          {/* ───────── Blocked ───────── */}
          <GuildBlockedTable
            projectId={id}
            guildId={guildId}
            guild={guild}
            onChanged={() => {
              void qc.invalidateQueries({
                queryKey: ["project", id, "activity"],
              });
            }}
          />

          {/* ───────── Chat ───────── */}
          <GuildChatFeed
            projectId={id}
            guildId={guildId}
            createdAt={guild.created_at}
          />

          {/* ───────── Danger zone ─────────
              Last in the column, below the chat, and visible to everyone who
              may moderate the guild: there is no separate right to disband
              one. The guilds list carries no delete button at all — this is
              the screen where you can see what you are about to remove. */}
          <div className={styles.dangerZoneGap} />
          <section className={styles.dangerZone} aria-label="Danger zone">
            <div className={styles.dzHead}>
              <AlertTriangle size={16} aria-hidden="true" />
              <h3>Danger zone</h3>
            </div>
            <div className={styles.dzRow}>
              <div className={styles.dzBody}>
                <h4>Delete guild</h4>
                <p>
                  Disbands the guild for good:{" "}
                  {guild.member_count === 1
                    ? "its one member loses it"
                    : `all ${guild.member_count} members lose it`}
                  , the chat history goes with it, and it drops out of the live
                  leaderboards. Everyone online is told straight away. This
                  cannot be undone.
                </p>
              </div>
              <Button
                variant="danger"
                onClick={() => setDeleteOpen(true)}
                icon={<Trash2 size={13} aria-hidden />}
              >
                Delete guild
              </Button>
            </div>
          </section>
        </div>
      </div>

      {deleteOpen && (
        <DeleteGuildDialog
          projectId={id}
          guild={guild}
          onClose={() => setDeleteOpen(false)}
          onDeleted={leaveForList}
        />
      )}
    </>
  );
}

// === Visibility badge ================================================ //
// Mirrors GuildsListPage's VisibilityBadge — the mockup `.vis-badge`.

function VisibilityBadge({ visibility }: { visibility: string }) {
  const tone = visibilityTone(visibility);
  return (
    <span className={`${styles.visBadge} ${styles[tone]}`}>
      <span className={styles.d} />
      {visibilityLabel(visibility)}
    </span>
  );
}

// === Loading skeleton =============================================== //
// Identity card + 4 section placeholders (Settings / Members / Blocked / Chat).

function LoadingSkeleton() {
  return (
    <>
      <div className={styles.card}>
        <div className={styles.cardBody}>
          <div className={styles.identity}>
            <Skeleton width={52} height={52} radius={13} />
            <div className={styles.skelLines}>
              <Skeleton width={160} height={18} />
              <Skeleton width={120} height={12} />
            </div>
          </div>
          <div className={styles.kvGrid}>
            {[0, 1, 2, 3, 4].map((i) => (
              <div className={styles.kv} key={i}>
                <Skeleton width={70} height={9} />
                <Skeleton width="80%" height={13} style={{ marginTop: 4 }} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {[0, 1, 2, 3].map((i) => (
        <div className={styles.card} key={i}>
          <div className={styles.skelHeader}>
            <Skeleton width={90} height={14} />
          </div>
          <div className={styles.cardBody}>
            <Skeleton width={120} height={12} />
            <Skeleton
              width="100%"
              height={36}
              radius={6}
              style={{ marginTop: 6 }}
            />
          </div>
        </div>
      ))}
    </>
  );
}
